import {
    AlertToastType,
    ButtonFlavor,
    ButtonType,
    ExButton,
    ExLink,
    LinkSize,
    ToastController,
} from '@boomi/exosphere';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { type CreateIdentityProvider, createIdentityProvider } from '../identityProviders';
import IdentityProviderFormFields, {
    type FormValues,
    EMPTY_FORM_VALUES,
} from './IdentityProviderFormFields';
import styles from '../../shared/Form.module.css';
import { GENERIC_ERROR_MESSAGE } from '../../shared/apiUtils';
import ErrorAlertBanner from '../../shared/ErrorAlertBanner';
import { trackSetIDP } from '../../pendo';

interface IdentityProviderCreateProps {
    onClose?: () => void;
}

const IdentityProviderCreate = ({ onClose }: IdentityProviderCreateProps = {}) => {
    const navigate = useNavigate();
    const close = onClose ?? (() => navigate('/identity-providers'));

    const [values, setValues] = useState<FormValues>(EMPTY_FORM_VALUES);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (patch: Partial<FormValues>) =>
        setValues((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setError(null);
        setSubmitting(true);

        const payload: CreateIdentityProvider = {
            name: values.name,
            allowedAudience: values.allowedAudience,
            clientId: values.clientId,
            clientSecret: values.clientSecret,
            wellKnownUrl: values.wellKnownUrl,
        };

        try {
            await createIdentityProvider(payload);
            trackSetIDP({ IDP_name: values.name, status: 'Success' });
            ToastController.show({
                description: `${values.name} created successfully.`,
                type: AlertToastType.SUCCESS,
            });
            close();
        } catch (err) {
            trackSetIDP({ IDP_name: values.name, status: 'Failed' });
            setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className={styles.header}>
                <ExLink
                    data-pendo-link="idp-create-back"
                    onClick={close}
                    leadingIcon={'Left arrow'}
                >
                    Identity Providers
                </ExLink>
                <h2 className={`${styles.heading} ex-text-h2`}>Add Identity Provider</h2>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
                <IdentityProviderFormFields values={values} onChange={handleChange} />

                {error && (
                    <ErrorAlertBanner className={styles.saveError} onClose={() => setError(null)}>
                        {error}
                    </ErrorAlertBanner>
                )}

                <div className={styles.actions}>
                    <ExLink
                        data-pendo-link="idp-create-cancel"
                        size={LinkSize.XSMALL}
                        onClick={close}
                    >
                        Cancel
                    </ExLink>
                    <ExButton
                        data-pendo-button="idp-create-addprovider"
                        type={ButtonType.PRIMARY}
                        flavor={ButtonFlavor.BRANDED}
                        disabled={submitting}
                        onClick={() => handleSubmit()}
                    >
                        {submitting ? 'Adding…' : 'Add Provider'}
                    </ExButton>
                </div>
            </form>
        </>
    );
};

export default IdentityProviderCreate;
